import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Tree, Button, Space, Modal, Spin } from "antd";
import {
  getBadgesChildren,
  createBadgeChild,
  updateBadgeChild,
  deleteBadgeChild,
} from "../../store/actions/badgesChildrenActions";
import { fetchBadges } from "../../store/actions/badgesActions";
import BadgeForm from "./BadgeForm";

const BadgesTree = (props) => {
  const {
    badges,
    badgeChild,
    badgesTree,
    loading,
    getBadgesChildren,
    createBadgeChild,
    updateBadgeChild,
    deleteBadgeChild,
    fetchBadges,
  } = props;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState("");
  const [badgePayload, setBadgePayload] = useState({
    position: 0,
    hidden: false,
    description: "",
    parentId: null,
    icons: { default: "" },
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedParentBadge, setSelectedParentBadge] = useState(null);
  const [modalKey, setModalKey] = useState(0); // Key para forçar remount do form

  useEffect(() => {
    const loadData = async () => {
      await getBadgesChildren();
      await fetchBadges();
    };
    loadData();
  }, [getBadgesChildren, fetchBadges]);

  const showModal = (type, badge, parentBadge) => {
    // Incrementar key para forçar remount do form (resolve bug de state leak)
    setModalKey(prev => prev + 1);

    if (type === "add") {
      setModalType("add");
      setSelectedParentBadge(parentBadge);
      setBadgePayload({
        position: 0,
        hidden: false,
        description: "",
        parentId: parentBadge?.id || null,
        icons: { default: "" },
        name: "",
      });
    }

    if (type === "edit" && badge) {
      setModalType("edit");
      setBadgePayload({
        ...badge,
        icons: {
          default: badge.icons?.default || badge.icons?.icon16x || "",
          locked: badge.icons?.locked || "",
        },
      });
    }

    if (type === "delete" && badge) {
      setBadgePayload(badge);
      setModalType("delete");
    }

    setIsModalVisible(true);
  };

  const handleOk = async () => {
    setActionLoading(true);

    try {
      if (modalType === "add") {
        await createBadgeChild(badgePayload);
      }

      if (modalType === "edit") {
        if (!badgePayload.id) {
          alert("Error: Badge ID is missing. Cannot update.");
          return;
        }
        await updateBadgeChild(badgePayload);
      }

      if (modalType === "delete") {
        await deleteBadgeChild(badgePayload.id);
      }

      setIsModalVisible(false);
      // Recarregar árvore
      await getBadgesChildren();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const mapBadgesToTreeData = (badge) => ({
    title: (
      <Space>
        <img
          src={badge.icons?.default || badge.icons?.icon16x || ""}
          alt={badge.name}
          style={{ width: 16, height: 16 }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        <span>
          <span style={{
            display: 'inline-block',
            minWidth: '24px',
            padding: '2px 6px',
            marginRight: '8px',
            background: '#f0f0f0',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#666',
            textAlign: 'center'
          }}>
            {badge.position ?? 0}
          </span>
          {badge.name}
          {badge.progress !== undefined && (
            <span style={{ marginLeft: 8, color: "#888" }}>
              ({Math.round(badge.progress)}%)
            </span>
          )}
          {badge.finished && (
            <span style={{ marginLeft: 8, color: "#52c41a" }}>✓</span>
          )}
          {badge.hidden && (
            <span style={{ marginLeft: 8, color: "#ff9800", fontSize: '11px' }}>🔒 Hidden</span>
          )}
        </span>
        <Button
          size="small"
          type="link"
          onClick={() => showModal("add", null, badge)}
        >
          Add Child
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => showModal("edit", badge)}
        >
          Edit
        </Button>
        <Button
          size="small"
          type="link"
          danger
          onClick={() => showModal("delete", badge)}
        >
          Delete
        </Button>
      </Space>
    ),
    key: badge.id,
    children: badge.children ? badge.children.map(mapBadgesToTreeData) : [],
  });

  // Usar dados do backend ao invés de hardcoded
  const treeData = badgesTree && Array.isArray(badgesTree)
    ? badgesTree.map(mapBadgesToTreeData)
    : [];

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Badges Tree</h2>
        <Button type="primary" onClick={() => showModal("add", null, null)}>
          Create Root Badge
        </Button>
      </div>
      {loading ? (
        <div style={{ padding: '50px 20px', textAlign: 'center' }}>
          <Spin tip="Loading badges tree..." size="large" />
        </div>
      ) : treeData.length === 0 ? (
        <div>
          <p>No badges found. Create your first badge to get started.</p>
        </div>
      ) : (
        <Tree
          treeData={treeData}
          defaultExpandAll
          showLine={{ showLeafIcon: false }}
          selectable={false}
        />
      )}

      <Modal
        title={
          modalType === "add"
            ? `Add badge${selectedParentBadge ? ` to ${selectedParentBadge.name}` : ""}`
            : modalType === "edit"
            ? `Edit badge`
            : `Delete badge`
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={actionLoading}
      >
        {(modalType === "add" || modalType === "edit") && (
          <BadgeForm
            key={modalKey}
            badgePayload={badgePayload}
            setBadgePayload={setBadgePayload}
            badges={badges}
            modalType={modalType}
            badgeChild={badgeChild}
            parentBadge={selectedParentBadge}
          />
        )}
        {modalType === "delete" && (
          <p>
            Are you sure you want to delete <strong>{badgePayload.name}</strong>?
            {badgePayload.children && badgePayload.children.length > 0 && (
              <span style={{ color: "red", display: "block", marginTop: 8 }}>
                Warning: This badge has {badgePayload.children.length} children that will also be affected.
              </span>
            )}
          </p>
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  badges: state.badges.badges || [],
  badgeChild: state.badgesChildren.badgeChild,
  badgesTree: state.badgesChildren.tree || [],
  loading: state.badgesChildren.loading || false,
});

export default connect(mapStateToProps, {
  fetchBadges,
  getBadgesChildren,
  createBadgeChild,
  updateBadgeChild,
  deleteBadgeChild,
})(BadgesTree);
